import { GridRowModel, GridRowParams } from '@mui/x-data-grid-pro';
import { includes } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { DataTable } from '@/components/tables/DataTable/DataTable';
import { ENTITY_TYPES } from '@/modules/entities/entities.constants';
import { DSUEModal } from '@/modules/estateTaxScenarios/modals/DSUEModal/DSUEModal';
import { ClientDetailsGiftingPage_ClientProfileFragment } from '@/modules/gifting/graphql/ClientDetailsGiftingPage.generated';
import { COLORS } from '@/styles/tokens/colors';
import { LifetimeExclusionEventKind } from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

import { LogNewGiftModal } from '../LogNewGiftModal/LogNewGiftModal';
import { RecordExemptionUsedModal } from '../RecordExemptionUsedModal/RecordExemptionUsedModal';
import {
  mapDataToRows,
  RowData,
  useColumns,
} from './ExemptionTrackingTable.utils';
import { ExemptionTrackingTable_LifetimeExclusionEventFragment } from './graphql/ExemptionTrackingTable.generated';

interface Props {
  lifetimeExclusionEvents: ExemptionTrackingTable_LifetimeExclusionEventFragment[];
  householdId: string;
  dsueClients?: ClientDetailsGiftingPage_ClientProfileFragment[];
}

const DEFAULT_MODAL_STATE = {
  kind: 'none',
} as const;

export function ExemptionTrackingTable({
  lifetimeExclusionEvents,
  householdId,
  dsueClients = [],
}: Props) {
  const [modalState, setModalState] = useState<{
    kind: 'gift' | 'exemption' | 'dsue' | 'none';
    actionKind?: 'edit' | 'duplicate';
    lifetimeExclusionEventId?: string;
    primaryClientId?: string;
  }>(DEFAULT_MODAL_STATE);

  const mapDsueClientsToRows = useCallback((): RowData[] => {
    return dsueClients
      .filter((client) => client.dsue?.amount.gt(0))
      .map((client) => {
        const dsue = client.dsue!;

        return {
          id: `dsue-${dsue.id}`,
          date: new Date(dsue.eventDate),
          grantor: `Deceased spouse of ${client.displayName}`,
          recipient: client.displayName,
          totalGiftAmount: null,
          lifetimeExemptionApplied: {
            lineOne: dsue.amount,
            lineTwo: '',
          },
          gstExemptionApplied: null,
          annualGiftExclusionApplied: null,
          kind: LifetimeExclusionEventKind.Adjustment,
          isDsue: true,
          primaryClientId: client.id,
          dsueId: dsue.id,
          householdId,
        };
      });
  }, [dsueClients, householdId]);

  const dsueRows = useMemo(
    () => mapDsueClientsToRows(),
    [mapDsueClientsToRows]
  );
  const regularRows = useMemo(
    () => mapDataToRows(lifetimeExclusionEvents, { householdId }),
    [lifetimeExclusionEvents, householdId]
  );

  const rows = useMemo(
    () => [...dsueRows, ...regularRows],
    [dsueRows, regularRows]
  );

  const handleEdit = (row: GridRowModel<RowData>) => {
    const kind = row.kind;
    const lifetimeExclusionEventId = row.id;

    if (
      'isDsue' in row &&
      row.isDsue &&
      'primaryClientId' in row &&
      row.primaryClientId
    ) {
      setModalState({
        kind: 'dsue',
        actionKind: 'edit',
        primaryClientId: row.primaryClientId,
      });
      return;
    }

    switch (kind) {
      case LifetimeExclusionEventKind.Gift:
        setModalState({
          kind: 'gift',
          actionKind: 'edit',
          lifetimeExclusionEventId,
        });
        break;
      case LifetimeExclusionEventKind.Adjustment:
        setModalState({
          kind: 'exemption',
          actionKind: 'edit',
          lifetimeExclusionEventId,
        });
        break;
      default:
        throw new UnreachableError({
          case: kind,
          message: `Unrecognized exclusion event kind ${kind}`,
        });
    }
  };

  const handleDuplicate = (row: GridRowModel<RowData>) => {
    const kind = row.kind;
    const lifetimeExclusionEventId = row.id;

    switch (kind) {
      case LifetimeExclusionEventKind.Gift:
        setModalState({
          kind: 'gift',
          actionKind: 'duplicate',
          lifetimeExclusionEventId,
        });
        break;
      case LifetimeExclusionEventKind.Adjustment:
        setModalState({
          kind: 'exemption',
          actionKind: 'duplicate',
          lifetimeExclusionEventId,
        });
        break;
      default:
        throw new UnreachableError({
          case: kind,
          message: `Unrecognized exclusion event kind ${kind}`,
        });
    }
  };

  const { columns } = useColumns({ handleDuplicate });

  const handleRowClick = (params: GridRowParams<RowData>) => {
    const row = params.row;

    // Skip non-editable entity types
    if (
      includes([ENTITY_TYPES.QPRT, ENTITY_TYPES.GRAT], row.recipientEntityType)
    ) {
      return;
    }

    handleEdit(row);
  };

  const getRowClassName = useCallback(
    (params: { row: RowData }) => {
      const row = params.row;
      if (!('isDsue' in row && row.isDsue) && dsueRows.length > 0) {
        const index = rows.findIndex((r) => r.id === params.row.id);
        const prevRow = index > 0 ? rows[index - 1] : undefined;
        if (prevRow && 'isDsue' in prevRow && prevRow.isDsue) {
          return 'dsue-separator-row';
        }
      }
      return '';
    },
    [dsueRows.length, rows]
  );

  const actionModals = useMemo(() => {
    if (modalState.kind === 'dsue') {
      if (!modalState.primaryClientId) {
        return null;
      }

      return (
        <DSUEModal
          householdId={householdId}
          primaryClientId={modalState.primaryClientId}
          variant={modalState.actionKind === 'edit' ? 'edit' : 'create'}
          onClose={() => {
            setModalState(DEFAULT_MODAL_STATE);
          }}
        />
      );
    }

    if (!modalState.lifetimeExclusionEventId) {
      return null;
    }

    return (
      <>
        <LogNewGiftModal
          isOpen={modalState.kind === 'gift'}
          onClose={() => {
            setModalState(DEFAULT_MODAL_STATE);
          }}
          householdId={householdId}
          lifetimeExclusionEventId={modalState.lifetimeExclusionEventId}
          isDuplicate={modalState.actionKind === 'duplicate'}
          isEdit={modalState.actionKind === 'edit'}
        />
        <RecordExemptionUsedModal
          isOpen={modalState.kind === 'exemption'}
          onClose={() => {
            setModalState(DEFAULT_MODAL_STATE);
          }}
          householdId={householdId}
          lifetimeExclusionEventId={modalState.lifetimeExclusionEventId}
          isDuplicate={modalState.actionKind === 'duplicate'}
          isEdit={modalState.actionKind === 'edit'}
        />
      </>
    );
  }, [householdId, modalState]);

  return (
    <>
      {actionModals}
      <DataTable
        getRowClassName={getRowClassName}
        onRowClick={handleRowClick}
        columnVisibilityModel={{
          kind: false,
        }}
        sx={{
          '& .dsue-separator-row': {
            borderTop: '2px solid',
            borderColor: COLORS.NAVY[600],
          },
        }}
        columns={columns}
        rows={rows}
      />
    </>
  );
}
